import { GetOneParams, GetOneResult, RaRecord } from 'react-admin';
import { getJson, postJson } from '../../../utils/api';
import IHandler from './IHandler';

/**
 * The four Speedboat fields credit-decision's banner ladder reads.
 *
 * Deliberately not the whole user record: the endpoint reads exactly these, so
 * sending more would put PII on the wire that nothing consumes.
 */
interface SpeedboatBannerFields {
  status?: string;
  signupStep?: string;
  hasKba?: boolean;
  kycStatus?: string;
}

/**
 * Speedboat's admin API answers "no such user" with **400**, not 404:
 * `ErrorResult.responseFromFailure` defaults to 400 and no controller in that
 * API emits 404 at all. The 404 arm is kept only so this keeps working if that
 * ever changes.
 *
 * `fetchApi` parses the body as JSON when the response says so and falls back
 * to text otherwise, so `error` can be either the parsed object or the raw
 * string — both are matched rather than assuming the happy shape.
 */
const isNoSuchUser = (e: any): boolean => {
  if (e?.status === 404) {
    return true;
  }
  if (e?.status !== 400) {
    return false;
  }

  const body = e?.error;
  if (typeof body === 'string') {
    return body.includes('Not Found');
  }
  return body?.errorType === 'Not Found';
};

/**
 * `hasKba` is **derived, never stored** — so it has to be derived here too.
 *
 * Nothing in Speedboat writes a `hasKba` column: it exists only as the value
 * `userService.convertUserModel` computes from `kbaAnswers`, which is what
 * `GET /me` returns and therefore what the customer-facing `GET /banner`
 * decides on. `GET /admin/user/{userId}` does NOT convert — it returns the raw
 * repository row, where `hasKba` is always `undefined`.
 *
 * Forwarding the raw field would send `hasKba: undefined` for every user, and
 * credit-decision's branch 5 requires it truthy — so the admin page could never
 * report SPEEDBOAT_ACCOUNT_ACTIVE, the most common suppression cause and one of
 * the three tickets SPEED-3701 was written to explain. Measured on dev: of 400
 * user rows, 0 carry the column and 286 carry `kbaAnswers`.
 *
 * Deriving it here rather than converting in the admin API keeps that endpoint's
 * response unchanged for its other consumers.
 */
const hasKba = (user: any): boolean => user?.kbaAnswers != null && Object.keys(user.kbaAnswers).length > 0;

export default class BannerHandler implements IHandler {
  static route = '/admin/banner';

  /**
   * The member's Speedboat record, or `null` when they have no card account.
   *
   * `userId` on Speedboat's admin API is the Bits membership number, the same
   * identifier the banner route is keyed on, so the id needs no translation.
   *
   * "No Speedboat account" is the answer, not a failure — the banner ladder has
   * a branch for exactly that, and members without a card account are the
   * population this diagnostic most often has to explain. Any OTHER error
   * propagates: guessing `null` there would report a visible banner for someone
   * who may be an active cardholder, which is the mistake the mandatory field
   * exists to prevent.
   */
  static async fetchSpeedboatRecord(id: string): Promise<SpeedboatBannerFields | null> {
    try {
      const response = await getJson(`/admin/user/${id}`);
      const { data } = await response.json();
      const { status, signupStep, kycStatus } = data;
      return { status, signupStep, kycStatus, hasKba: hasKba(data) };
    } catch (e: any) {
      if (isNoSuchUser(e)) {
        return null;
      }
      throw e;
    }
  }

  /**
   * SPEED-3701 made this a POST that carries the Speedboat record.
   *
   * credit-decision cannot fetch it itself: prod's Speedboat admin API is a
   * private API Gateway reachable only through a VPC endpoint its Lambdas are
   * not attached to. The panel already holds the record, so it supplies it.
   * `speedboat` is mandatory — omitting it is a 400, and `null` is the explicit
   * way to say "I looked, there is no account".
   */
  static async getOneHandler<RecordType extends RaRecord = any>(
    resource: string,
    params?: GetOneParams<any>
  ): Promise<GetOneResult<RecordType>> {
    const id = params?.id;
    const speedboat = await this.fetchSpeedboatRecord(String(id));

    return postJson(`${this.route}/${id}`, { speedboat }, process.env.REACT_APP_BASE_CREDIT_DECISION_API_URL).then(
      async (response) => {
        const { data } = await response.json();

        // react-admin identifies the record by `id`, and the member is the
        // record here, so the route id is its identity. The previous
        // `eligibilityId` was never a top-level field on this response — it
        // lives under `evidence.eligibility` — so `id` had always been
        // undefined.
        return {
          data: {
            ...data,
            id,
          },
        };
      }
    );
  }
}
